import React from 'react';
import { PortalField } from '../FormField/PortalField';
import { PortalInput } from '../FormField/PortalInput';
import { browserTimezone, dateInputBounds, formatScheduledFor, combineDateTime } from '../../lib/scheduling';
import styles from './ScheduleStep.module.scss';

interface ScheduleStepProps {
  /** What is being scheduled, for the summary line (e.g. "Adoption", "Foster placement"). */
  actionLabel: string;
  date: string;
  time: string;
  onDateChange: (date: string) => void;
  onTimeChange: (time: string) => void;
  /** Validation message from `scheduleValidationError`, shown once both fields are filled. */
  error?: string | null;
  /**
   * Drops the "nothing happens until then" reassurance and the timezone hint. Set when rescheduling,
   * where the rescue already booked this once and the surrounding copy states the current time.
   */
  compact?: boolean;
  /** Extra context above the fields — usually which pet and collar this applies to. */
  children?: React.ReactNode;
}

/**
 * Date/time picker for booking a hand-off. Native `date`/`time` inputs rather than a calendar
 * component: the portal has no date picker and no date-picker dependency, and these give the platform
 * keyboard and mobile pickers for free.
 */
export default function ScheduleStep({
  actionLabel,
  date,
  time,
  onDateChange,
  onTimeChange,
  error,
  compact,
  children,
}: ScheduleStepProps) {
  const bounds = dateInputBounds();
  const preview = combineDateTime(date, time);

  return (
    <div className={styles.step}>
      {children}

      <div className={styles.fieldRow}>
        <PortalField label="Date *">
          <PortalInput
            type="date"
            value={date}
            min={bounds.min}
            max={bounds.max}
            hasError={!!error}
            onChange={(e) => onDateChange(e.target.value)}
          />
        </PortalField>
        <PortalField label="Time *">
          <PortalInput type="time" value={time} hasError={!!error} onChange={(e) => onTimeChange(e.target.value)} />
        </PortalField>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {!error && preview && (
        <div className={styles.preview}>
          {compact ? (
            <>
              Runs <strong>{formatScheduledFor(preview.scheduledFor, preview.scheduledForTimezone)}</strong>.
            </>
          ) : (
            <>
              {actionLabel} will run automatically on{' '}
              <strong>{formatScheduledFor(preview.scheduledFor, preview.scheduledForTimezone)}</strong>. Nothing changes
              until then — the collar stays with the rescue and no email or text goes out.
            </>
          )}
        </div>
      )}

      {!compact && (!date || !time) && (
        <div className={styles.hint}>Times are in your local time zone ({browserTimezone()}).</div>
      )}
    </div>
  );
}
